/* eslint-disable class-methods-use-this */
import {
  ExtendedTextNode,
  SerializedExtendedTextNode,
} from '@mutiny-pkg/bin-ui/src/richtext/ExtendedTextPlugin/ExtendedTextNode';

import {
  $applyNodeReplacement,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type EditorConfig,
  type NodeKey,
  type Spread,
} from 'lexical';

export type SerializedLookupNode = Spread<
  {
    label?: string;
    linkColumnId: string;
    refLookupColumnId: string;
  },
  SerializedExtendedTextNode
>;

function $convertLookupElement(domNode: HTMLElement): DOMConversionOutput | null {
  const { textContent, attributes } = domNode;
  const linkColumnId = attributes.getNamedItem('data-lexical-lookup-link-column-id')?.value;
  const refLookupColumnId = attributes.getNamedItem('data-lexical-lookup-lookup-column-id')?.value;

  if (textContent !== null && linkColumnId && refLookupColumnId) {
    const node = $createLookupNode(linkColumnId, refLookupColumnId, textContent);
    return {
      node,
    };
  }

  return null;
}

export class LookupNode extends ExtendedTextNode {
  __linkColumnId: string;

  __refLookupColumnId: string;

  __label: string;

  static getType(): string {
    return 'lookup';
  }

  static clone(node: LookupNode): LookupNode {
    return new LookupNode(node.__linkColumnId, node.__refLookupColumnId, node.__label, node.__key);
  }

  static importJSON(serializedNode: SerializedLookupNode): LookupNode {
    const node = $createLookupNode(serializedNode.linkColumnId, serializedNode.refLookupColumnId, serializedNode.label);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setStyle(serializedNode.style);
    node.setHighlightColor(serializedNode.highlightColor);
    node.setFontColor(serializedNode.fontColor);
    return node;
  }

  constructor(linkColumnId: string, refLookupColumnId: string, label?: string, key?: NodeKey) {
    const textContent = label ?? '';
    super(textContent, key);
    this.__linkColumnId = linkColumnId;
    this.__refLookupColumnId = refLookupColumnId;
    this.__label = label ?? this.getRawLabel();
  }

  getValue(): string {
    return `${this.__linkColumnId}.${this.__refLookupColumnId}`;
  }

  getLabel(): string | undefined {
    return this.__label;
  }

  setLabel(label: string): void {
    this.__text = label;
    this.__label = label;
  }

  // Label of just raw values without hydration
  getRawLabel(): string {
    return `${this.__linkColumnId}.${this.__refLookupColumnId}`;
  }

  /**
   * Check if this node's textContent is consistent with the lookup value. A node can become inconsistent
   * if the lookup value is changed by the editor, such when a segment of the label is deleted.
   */
  isConsistent(): boolean {
    return this.getRawLabel() === this.__label || this.__text === this.__label;
  }

  exportJSON(): SerializedLookupNode {
    return {
      ...super.exportJSON(),
      linkColumnId: this.__linkColumnId,
      refLookupColumnId: this.__refLookupColumnId,
      label: this.__label,
      type: 'lookup',
      version: 1,
    };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.className = `lookup ${config.theme.mention} ${dom.className}`;
    dom.spellcheck = false;
    dom.textContent = this.__label;
    return dom;
  }

  updateDOM(prevNode: ExtendedTextNode, dom: HTMLElement, config: EditorConfig): boolean {
    const isUpdated = super.updateDOM(prevNode, dom, config);
    // eslint-disable-next-line no-param-reassign
    dom.textContent = this.__label;
    return isUpdated;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-lookup', 'true');
    element.setAttribute('data-lexical-lookup-link-column-id', this.__linkColumnId);
    element.setAttribute('data-lexical-lookup-lookup-column-id', this.__refLookupColumnId);
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-lookup')) {
          return null;
        }
        return {
          conversion: $convertLookupElement,
          priority: 1,
        };
      },
    };
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return false;
  }
}

function $createLookupNode(linkColumnId: string, refLookupColumnId: string, label?: string): LookupNode {
  const node = new LookupNode(linkColumnId, refLookupColumnId, label);
  return $applyNodeReplacement(node);
}
